import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react'

interface PullToRefreshProps {
  children: ReactNode
  className?: string
  disabled?: boolean
}

export function PullToRefresh({ children, className, disabled = false }: PullToRefreshProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const startY = useRef(0)
  const pulling = useRef(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  // Pulling past the threshold only arms the refresh; the finger has to stay
  // down for HOLD_MS before release reloads. A quick over-scroll while
  // navigating a long list used to reload the page and lose the position.
  const [armed, setArmed] = useState(false)
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const THRESHOLD = 80
  const MAX_PULL = 120
  const HOLD_MS = 1200

  const clearHold = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
    setArmed(false)
  }, [])

  useEffect(() => () => { if (holdTimer.current) clearTimeout(holdTimer.current) }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return
    const el = containerRef.current
    if (!el || el.scrollTop > 0 || refreshing) return
    // React synthetic events bubble through the React tree, not the DOM —
    // so touches inside portaled content (Radix Dialog/Sheet/Popover) still
    // hit this handler. Skip when the actual touch target isn't a DOM
    // descendant of our container.
    if (!el.contains(e.target as Node)) return
    startY.current = e.touches[0].clientY
    pulling.current = true
  }, [refreshing, disabled])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pulling.current) return
    const delta = e.touches[0].clientY - startY.current
    if (delta < 0) {
      pulling.current = false
      setPullDistance(0)
      clearHold()
      return
    }
    // Dampen the pull
    const dampened = Math.min(delta * 0.5, MAX_PULL)
    setPullDistance(dampened)
    if (dampened >= THRESHOLD) {
      if (!holdTimer.current) {
        holdTimer.current = setTimeout(() => {
          holdTimer.current = null
          setArmed(true)
        }, HOLD_MS)
      }
    } else if (holdTimer.current || armed) {
      clearHold()
    }
  }, [armed, clearHold])

  const handleTouchEnd = useCallback(() => {
    if (!pulling.current) return
    pulling.current = false

    if (armed && pullDistance >= THRESHOLD) {
      clearHold()
      setRefreshing(true)
      setPullDistance(THRESHOLD * 0.5)
      // Reload the page
      window.location.reload()
    } else {
      clearHold()
      setPullDistance(0)
    }
  }, [armed, pullDistance, clearHold])

  if (disabled) {
    return <div className={className}>{children}</div>
  }

  return (
    <div
      ref={containerRef}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-200 ease-out"
        style={{ height: pullDistance > 0 ? pullDistance : 0 }}
      >
        <div className="flex flex-col items-center gap-1 text-muted-foreground">
          {refreshing ? (
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 transition-transform"
              style={{ transform: armed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          )}
          <span className="text-xs">
            {refreshing
              ? 'Refreshing...'
              : armed
                ? 'Release to refresh'
                : pullDistance >= THRESHOLD
                  ? 'Keep holding...'
                  : 'Pull to refresh'}
          </span>
        </div>
      </div>

      {children}
    </div>
  )
}
